import { useState, useMemo } from "react";

/**
 * JumpToDateModal — anchor the chart to a precise date + session.
 * Calls onJump(targetIso, session) with a UTC ISO timestamp.
 */
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const SESSIONS = [
  { key: "Asia",     label: "Asia Open (20:00 ET)",  hour: 20 },
  { key: "London",   label: "London Open (02:00 ET)", hour: 2 },
  { key: "New York", label: "NY Open (09:30 ET)",    hour: 9, minute: 30 },
  { key: "NY Close", label: "NY Close (16:00 ET)",   hour: 16 },
];

export default function JumpToDateModal({ onClose, onJump, periodDays = 30 }) {
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [day, setDay]     = useState(today.getDate());
  const [sessionIdx, setSessionIdx] = useState(2); // NY Open default

  const minDate = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - periodDays); return d;
  }, [periodDays]);

  function jump() {
    const session = SESSIONS[sessionIdx];
    // Build EST anchor — yfinance bars are UTC; convert EST→UTC (~+4h DST or +5h EST).
    const local = new Date(year, month, day, session.hour, session.minute || 0, 0);
    onJump(local.toISOString(), session.key);
  }

  const within = (() => {
    const sel = new Date(year, month, day);
    return sel >= minDate && sel <= today;
  })();

  return (
    <div data-testid="jump-modal" className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 font-mono">
      <div className="bg-[#0f131b] border border-slate-800 rounded-2xl max-w-md w-full p-5 shadow-2xl">
        <div className="flex justify-between items-start mb-3 border-b border-slate-800 pb-2">
          <div>
            <h2 className="text-sm font-bold text-white"><i className="fa-solid fa-calendar-plus mr-1 text-blue-300"/> Jump to Date</h2>
            <p className="text-[10px] text-slate-500">Snap the chart to a specific historical session. Limit: {periodDays} days back.</p>
          </div>
          <button data-testid="jump-close" onClick={onClose} className="text-slate-500 hover:text-rose-300"><i className="fa-solid fa-xmark"/></button>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <Col label="Year">
            <select data-testid="jd-year" value={year} onChange={e => setYear(parseInt(e.target.value))}
              className="w-full px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-sm">
              {[today.getFullYear(), today.getFullYear() - 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </Col>
          <Col label="Month">
            <select data-testid="jd-month" value={month} onChange={e => setMonth(parseInt(e.target.value))}
              className="w-full px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-sm">
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          </Col>
          <Col label="Day">
            <select data-testid="jd-day" value={day} onChange={e => setDay(parseInt(e.target.value))}
              className="w-full px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-sm">
              {Array.from({length: 31}, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Col>
        </div>
        <Col label="Session">
          <select data-testid="jd-session" value={sessionIdx} onChange={e => setSessionIdx(parseInt(e.target.value))}
            className="w-full px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-sm">
            {SESSIONS.map((s, i) => <option key={s.key} value={i}>{s.label}</option>)}
          </select>
        </Col>
        {!within && (
          <div className="mt-3 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5">
            <i className="fa-solid fa-triangle-exclamation mr-1"/> Date outside your {periodDays}-day tier window. Upgrade to access deeper history.
          </div>
        )}
        <button data-testid="jd-go" onClick={jump} disabled={!within}
          className="w-full mt-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-xs font-bold">
          <i className="fa-solid fa-arrow-right-to-bracket mr-1"/> Jump
        </button>
      </div>
    </div>
  );
}

function Col({ label, children }) {
  return (
    <div>
      <div className="text-[9px] text-slate-500 uppercase tracking-widest mb-1">{label}</div>
      {children}
    </div>
  );
}
